import type {
  ArmDefinition,
  ExperimentArm,
  FrogFactor,
  SplitResult,
  WishMessage,
} from "./types";

export const ARMS: Record<ExperimentArm, ArmDefinition> = {
  A: { frog: "fixed", wish: "plain" },
  B: { frog: "random", wish: "plain" },
  C: { frog: "fixed", wish: "tactile" },
  D: { frog: "random", wish: "tactile" },
};

export const WISH_MESSAGES: readonly WishMessage[] = [
  { id: "prosperity-grow", tone: "encouraging", text: "May a small money win grow your prosperity today." },
  { id: "useful-opportunity", tone: "encouraging", text: "May a timely idea bring you a profit today." },
  { id: "welcome-saving", tone: "encouraging", text: "May an unexpected discount keep more money with you today." },
  { id: "welcome-reward", tone: "encouraging", text: "May your effort bring a welcome cash reward today." },
  { id: "lucky-deal", tone: "encouraging", text: "May a lucky deal bring you a real money benefit today." },
  { id: "money-stretch", tone: "encouraging", text: "May your money stretch a little further today." },
  { id: "avoided-fee", tone: "encouraging", text: "May avoiding an unnecessary fee keep more money with you today." },
  { id: "better-value", tone: "encouraging", text: "May something you need find you at a better price today." },
  { id: "extra-money", tone: "encouraging", text: "May a small opportunity bring welcome extra money today." },
  { id: "generous-reward", tone: "encouraging", text: "May a money bonus exceed your expectations today." },
] as const;

export function isExperimentArm(value: unknown): value is ExperimentArm {
  return value === "A" || value === "B" || value === "C" || value === "D";
}

export function randomArm(randomValue = secureRandom()): ExperimentArm {
  const index = Math.min(3, Math.floor(clampUnit(randomValue) * 4));
  return (["A", "B", "C", "D"] as const)[index] ?? "A";
}

export function createSplit(
  amountCents: number,
  factor: FrogFactor,
  randomValue = secureRandom(),
): SplitResult {
  if (!Number.isInteger(amountCents) || amountCents < 2_000 || amountCents > 20_000) {
    throw new RangeError(
      "The ritual pot must be an integer number of minor currency units between 20.00 and 200.00.",
    );
  }

  const enjoyPercent =
    factor === "fixed" ? 60 : 55 + Math.min(10, Math.floor(clampUnit(randomValue) * 11));
  const enjoyCents = Math.round((amountCents * enjoyPercent) / 100);
  const keepCents = amountCents - enjoyCents;

  return {
    amountCents,
    enjoyPercent,
    keepPercent: 100 - enjoyPercent,
    enjoyCents,
    keepCents,
  };
}

export function amountBand(amountCents: number): "20-49" | "50-99" | "100-149" | "150-200" {
  if (amountCents < 5_000) return "20-49";
  if (amountCents < 10_000) return "50-99";
  if (amountCents < 15_000) return "100-149";
  return "150-200";
}

export function sanitizeSource(value: string | null): string {
  if (!value) return "direct";
  const sanitized = value.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 40);
  return sanitized || "direct";
}

export function canonicalizeMarketTestSearch(search: URLSearchParams): string {
  const canonical = new URLSearchParams();
  const language = search.get("lang");
  if (language === "en" || language === "uk") canonical.set("lang", language);

  const source = sanitizeSource(search.get("source"));
  if (source !== "direct") canonical.set("source", source);
  if (search.get("motion") === "reduce") canonical.set("motion", "reduce");
  if (search.get("debug") === "1") canonical.set("debug", "1");
  if (search.get("qa") === "1") canonical.set("qa", "1");

  const arm = search.get("arm");
  if (isExperimentArm(arm)) canonical.set("arm", arm);
  return canonical.toString();
}

export function chooseWish(participantId: string, localDay: string, potCounter: number): WishMessage {
  const seed = `${participantId}:${localDay}:${potCounter}`;
  const index = stableHash(seed) % WISH_MESSAGES.length;
  return WISH_MESSAGES[index] ?? WISH_MESSAGES[0]!;
}

export function durationBand(milliseconds: number): "under-15s" | "15-30s" | "30-60s" | "over-60s" {
  if (milliseconds < 15_000) return "under-15s";
  if (milliseconds < 30_000) return "15-30s";
  if (milliseconds < 60_000) return "30-60s";
  return "over-60s";
}

export function daysSinceBand(previousIso: string | undefined): "same-day" | "1-day" | "2-6-days" | "7-plus-days" {
  if (!previousIso) return "same-day";
  const elapsedDays = Math.floor((Date.now() - new Date(previousIso).getTime()) / 86_400_000);
  if (elapsedDays <= 0) return "same-day";
  if (elapsedDays === 1) return "1-day";
  if (elapsedDays < 7) return "2-6-days";
  return "7-plus-days";
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(0.999999999, Math.max(0, value));
}

function secureRandom(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return (values[0] ?? 0) / 4_294_967_296;
}
